import { AlertCircle, Download, FileCode } from 'lucide-react';
import { useMemo, useRef } from 'react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';

import { QR_CODE_TIPS } from '../config/styles';
import { describeQrError, type QrGenerationResult } from '../domain/generator';
import { useImageFile } from '../hooks/useImageFile';
import { paintToCanvas } from '../render/canvas';
import { downloadCanvasAsPng, downloadSvg, toFilenameStem } from '../render/download';
import { buildDrawModel } from '../render/model';
import { renderToSvg } from '../render/svg';
import { useQrContext } from '../state/qr-context';
import { QrCanvas } from './QrCanvas';

/**
 * Fixed multiplier for PNG export, so a downloaded file is identical whether
 * it was produced on a HiDPI laptop or an external monitor.
 */
const PNG_EXPORT_SCALE = 2;

export function PreviewPanel({ result }: { readonly result: QrGenerationResult }) {
  const { design } = useQrContext();
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const centerImage = useImageFile(design.centerImage);
  const moduleImage = useImageFile(design.customModuleImage);

  const model = useMemo(() => {
    if (!result.ok) return null;
    return buildDrawModel(result.matrix, design, {
      hasCenterImage: centerImage.image !== null,
      hasModuleImage: moduleImage.image !== null,
    });
  }, [result, design, centerImage.image, moduleImage.image]);

  const filenameStem = toFilenameStem(design.content);

  const handlePngDownload = (): void => {
    if (model === null) return;
    // Render offscreen at a fixed scale rather than reusing the on-screen
    // canvas, whose resolution depends on the viewer's display.
    const exportCanvas = document.createElement('canvas');
    paintToCanvas(
      exportCanvas,
      model,
      {
        centerImage: centerImage.image?.element ?? null,
        moduleImage: moduleImage.image?.element ?? null,
      },
      PNG_EXPORT_SCALE,
    );
    void downloadCanvasAsPng(exportCanvas, `${filenameStem}.png`);
  };

  const handleSvgDownload = (): void => {
    if (model === null) return;
    const svg = renderToSvg(model, {
      centerImageHref: centerImage.image?.dataUrl ?? null,
      moduleImageHref: moduleImage.image?.dataUrl ?? null,
    });
    downloadSvg(svg, `${filenameStem}.svg`);
  };

  return (
    <div className="sticky top-6 space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Vorschau</CardTitle>
        </CardHeader>

        <CardContent className="space-y-3">
          {model === null ? (
            <div className="flex min-h-56 items-center justify-center rounded-lg border border-dashed p-6">
              <Alert variant="destructive" className="border-0 bg-transparent">
                <AlertCircle aria-hidden="true" />
                <AlertDescription>
                  {result.ok ? 'Kein QR-Code verfügbar.' : describeQrError(result.error)}
                </AlertDescription>
              </Alert>
            </div>
          ) : (
            <QrCanvas
              model={model}
              canvasRef={canvasRef}
              centerImage={centerImage.image?.element ?? null}
              moduleImage={moduleImage.image?.element ?? null}
              alt={`QR-Code für ${design.content}`}
            />
          )}

          {(centerImage.error ?? moduleImage.error) !== null && (
            <Alert variant="destructive">
              <AlertCircle aria-hidden="true" />
              <AlertDescription>{centerImage.error ?? moduleImage.error}</AlertDescription>
            </Alert>
          )}

          <p className="text-center text-sm text-muted-foreground">
            Scanne den Code, um die Lesbarkeit zu prüfen.
          </p>
        </CardContent>

        <CardFooter>
          <div className="flex w-full flex-col gap-2">
            <Button onClick={handlePngDownload} disabled={model === null} className="w-full">
              <Download aria-hidden="true" />
              Als PNG herunterladen
            </Button>
            <Button
              variant="outline"
              onClick={handleSvgDownload}
              disabled={model === null}
              className="w-full"
            >
              <FileCode aria-hidden="true" />
              Als SVG herunterladen (Vektor)
            </Button>
          </div>
        </CardFooter>
      </Card>

      <Card className="bg-muted">
        <CardHeader>
          <CardTitle className="text-base">Tipps</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2 text-sm text-muted-foreground">
            {QR_CODE_TIPS.map((tip) => (
              <li key={tip} className="flex gap-2">
                <span
                  aria-hidden="true"
                  className="mt-2 h-1 w-1 shrink-0 rounded-full bg-primary"
                />
                <span>{tip}</span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
